import { Component } from '@theme/component';

const STEP_KEYS = {
  1: 'scents',
  2: 'engraving_option',
  3: 'theme',
  4: 'design',
  5: 'engraving_text',
  6: 'review',
  7: 'upsell',
  8: 'upload_design',
  9: 'candle_preview',
};

const STEP_EVENTS = {
  1: 'personalized_steps_scents_seen',
  2: 'personalized_steps_engraving_option_seen',
  3: 'personalized_steps_theme_seen',
  4: 'personalized_steps_design_seen',
  5: 'personalized_steps_engraving_text_seen',
  6: 'personalized_steps_review_seen',
  7: 'personalized_steps_upsell_seen',
  8: 'personalized_steps_upload_design_seen',
  9: 'personalized_steps_candle_preview_seen',
};

const seenStepsThisTab = new Set();

/**
 * Personalized Steps Popup Component - Hardcoded 6 Steps
 * 
 * Step 1: Choose Scents (Required, max 3)
 * Step 2: Select your engraving option (Required)
 * Step 3: Select Theme (Skippable)
 * Step 4: Choose Design (Skippable)
 * Step 5: Text to Engrave (Skippable)
 * Step 6: Review & Confirm
 * Step 8: Upload Custom Design (For "My Design" option)
 */
export class PersonalizedStepsPopupComponent extends Component {
  requiredRefs = [
    'triggerButton',
    'popup',
    'overlay',
    'stepContainer',
    'prevButton',
    'nextButton',
    'closeButton',
    'zoomModal',
    'zoomOverlay',
    'zoomImage',
    'zoomTitle',
    'zoomCloseButton',
    'uploadModal',
    'uploadOverlay',
    'uploadFileInput',
    'uploadPreviewContainer',
    'uploadPreviewImage',
    'uploadPdfPlaceholder',
    'uploadPdfFilename',
    'uploadArea',
    'uploadDropzone',
    'uploadConfirmButton',
    'uploadCancelButton',
    'uploadCloseButton',
    'uploadRemoveButton',
    'uploadDesignRadio',
    'uploadPlaceholder',
    'uploadPreview',
    'upsellQuantityWrapper',
    'upsellQuantityInput',
    'upsellQuantityMinus',
    'upsellQuantityPlus',
    'upsellQuantityWarning',
    'candlePreviewContainer',
    // Note: 'upsellButton' is optional - only present when upsell product is configured
  ];

  // Hardcoded: 6 total steps
  currentStep = 1;
  // totalSteps = 6; // Calculated dynamically based on enabled steps
  
  // Track completed actions in steps (for auto-progression)
  completedStepActions = new Set();
  
  // Collected data
  selectedScents = [];
  selectedEngravingOption = {
    value: null,
    label: null
  };
  selectedTheme = {
    value: null,
    label: null
  };
  /**
   * @type {Array<{value: string, label: string}> | {value: string | null, label: string | null} | null}
   */
  selectedDesign = []; // Changed to array to support multiple selections
  engravingText = '';
  selectedQuantity = 1;
  uploadedFile = null;
  validationErrorMessage = null;
  logoLinkId = null; // Unique ID to link product with custom design product

  // Upsell configuration
  upsellSelected = false;
  upsellVariantId = null;
  upsellProductTitle = null;
  upsellLinkId = null; // Unique ID to link main product with upsell product
  upsellQuantity = 1;

  // Custom design configuration
  customDesignVariantId = null;

  // Multi-select configuration
  enableMultipleDesigns = false;
  maxDesigns = 1;

  // Track design selection order for proper FIFO behavior
  designSelectionOrder = [];

  // Translations
  translations = {};
  popupInstanceId = null;
  hasTrackedCompletionForJourney = false;

  connectedCallback() {
    super.connectedCallback();
    this.loadTranslations();
    this.loadDesignConfiguration();
    this.loadUpsellConfiguration();
    this.loadStepConfiguration(); // Must be after loadUpsellConfiguration to check upsellVariantId
    this.setupEventListeners();
    this.updateUI();
  }

  loadDesignConfiguration() {
    this.enableMultipleDesigns = this.getAttribute('data-enable-multiple-designs') === 'true';
    const maxDesignsAttr = this.getAttribute('data-max-designs');
    this.maxDesigns = maxDesignsAttr ? parseInt(maxDesignsAttr) : 1;
  }

  loadUpsellConfiguration() {
    this.upsellVariantId = this.getAttribute('data-upsell-variant-id');
    this.upsellProductTitle = this.getAttribute('data-upsell-product-title');
    this.customDesignVariantId = this.getAttribute('data-custom-design-variant-id');
  }

  loadStepConfiguration() {
    this.stepConfiguration = {};
    // Steps 1 to 9
    [1, 2, 3, 4, 5, 6, 7, 8, 9].forEach(step => {
      // Use getAttribute to avoid ambiguity with dataset property naming for numbered attributes
      const attrName = `data-step-${step}-enabled`;
      const value = this.getAttribute(attrName);
      // Default to true if attribute is missing, but it should be present from Liquid
      this.stepConfiguration[step] = value !== 'false';
    });

    // Step 7 (upsell) also requires upsell product to be configured
    if (!this.upsellVariantId) {
      this.stepConfiguration[7] = false;
    }
  }

  isStepEnabled(step) {
    return this.stepConfiguration[step] === true;
  }

  loadTranslations() {
    try {
      const translationsAttr = this.dataset.translations;
      if (translationsAttr) {
        this.translations = JSON.parse(translationsAttr);
      }
    } catch (error) {
      console.error('Failed to parse translations:', error);
      // Fallback to English defaults
      this.translations = {
        nextStep: 'Next Step',
        skipStep: 'Skip Step',
        seeAllImages: 'See all images',
        addToCart: 'Add to Cart',
        validationError: 'Please select at least one scent to continue.',
        engravingSimulationNotice: 'The simulation is done for only 1 candle.',
        progressIndicator: 'Step {{ step }} of {{ total }}',
        notSelected: 'Not selected',
        notProvided: 'Not provided',
        itemCount: {
          one: '{{ count }} item',
          other: '{{ count }} items'
        },
        errors: {
          formNotFound: 'Unable to add to cart. Please try again.',
          invalidFileType: 'Please upload a valid image file (JPG, PNG, GIF, or WEBP)',
          fileTooLarge: 'File size must be less than 20MB'
        },
        upload: {
          customDesign: 'Custom Design',
          uploadDesign: 'Upload Design',
          modalTitle: 'Upload Your Custom Design',
          modalDescription: 'Choose an image file (JPG, PNG, or GIF) for your custom design',
          clickToUpload: 'Click to upload',
          dragAndDrop: 'or drag and drop',
          fileHint: 'JPG, PNG, GIF or WEBP (MAX. 20MB)',
          remove: 'Remove',
          cancel: 'Cancel',
          confirm: 'Confirm Upload',
          closeAriaLabel: 'Close upload',
          previewAlt: 'Custom design preview',
          pdfSelected: 'PDF file selected successfully',
          fileName: 'File name:'
        }
      };
    }
  }

  t(key, replacements = {}) {
    let translation = this.translations[key] || key;
    
    // Handle nested keys like 'itemCount.one'
    if (key.includes('.')) {
      const keys = key.split('.');
      translation = keys.reduce((obj, k) => obj?.[k], this.translations) || key;
    }

    // Decode HTML entities that Liquid's | t filter may introduce
    const textarea = document.createElement('textarea');
    textarea.innerHTML = translation;
    translation = textarea.value;
    
    // Replace placeholders like [step] or {{ step }} with actual values
    Object.keys(replacements).forEach(placeholder => {
      // Support both [placeholder] and {{ placeholder }} formats
      translation = translation.replace(
        new RegExp(`\\[\\s*${placeholder}\\s*\\]`, 'g'),
        replacements[placeholder]
      );
      translation = translation.replace(
        new RegExp(`{{\\s*${placeholder}\\s*}}`, 'g'),
        replacements[placeholder]
      );
    });
    
    return translation;
  }

  setupEventListeners() {
    const { triggerButton, closeButton, overlay, prevButton, nextButton } = this.refs;

    // Open/Close
    if (triggerButton && !Array.isArray(triggerButton)) {
      triggerButton.addEventListener('click', () => this.openPopup());
    }
    if (closeButton && !Array.isArray(closeButton)) {
      closeButton.addEventListener('click', () => this.closePopup());
    }
    if (overlay && !Array.isArray(overlay)) {
      overlay.addEventListener('click', () => this.closePopup());
    }

    // Navigation
    if (prevButton && !Array.isArray(prevButton)) {
      prevButton.addEventListener('click', () => this.previousStep());
    }
    if (nextButton && !Array.isArray(nextButton)) {
      nextButton.addEventListener('click', () => this.handleNextClick());
    }

    // Keyboard
    document.addEventListener('keydown', (e) => {
      if (this.dataset.open !== undefined) {
        if (e.key === 'Escape') this.closePopup();
        //if (e.key === 'ArrowLeft' && this.currentStep > 1) this.previousStep();
        //if (e.key === 'ArrowRight' && this.currentStep < 5) this.handleNextClick();
      }
    });

    // Step-specific listeners
    this.setupStepListeners();
  }

  setupStepListeners() {
    // Step 1: Scent selection (max 3)
    const scentCheckboxes = this.querySelectorAll('input[name^="properties[_scents_"]');
    scentCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.handleScentChange());
    });

    // Step 2: Engraving option selection
    const engravingOptionRadios = this.querySelectorAll('input[name="properties[_engraving_option]"]');
    engravingOptionRadios.forEach(radio => {
      radio.addEventListener('change', () => this.handleEngravingOptionChange());
    });

    // Step 3: Theme selection
    const themeRadios = this.querySelectorAll('input[name="properties[_theme]"]');
    themeRadios.forEach(radio => {
      radio.addEventListener('change', () => this.handleThemeChange());
    });

    // Step 4: Design selection
    const designRadios = this.querySelectorAll('input[name="properties[_design]"]');
    designRadios.forEach(radio => {
      radio.addEventListener('change', (e) => this.handleDesignChange(e));
    });

    // Step 4: Design zoom buttons
    const zoomButtons = this.querySelectorAll('.design-zoom-button, .upsell-product-card__image');
    zoomButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleZoomClick(e));
    });

    // Step 4: Custom design upload
    this.setupUploadListeners();

    // Step 5: Engraving input(s)
    this.setupEngravingInputListeners();

    // Step 7: Upsell button
    this.setupUpsellListeners();

    // Zoom modal close handlers
    const { zoomCloseButton, zoomOverlay } = this.refs;
    if (zoomCloseButton && !Array.isArray(zoomCloseButton)) {
      zoomCloseButton.addEventListener('click', () => this.closeZoomModal());
    }
    if (zoomOverlay && !Array.isArray(zoomOverlay)) {
      zoomOverlay.addEventListener('click', () => this.closeZoomModal());
    }
  }

  // Step 1: Scent handlers
  handleScentChange() {
    const checked = Array.from(this.querySelectorAll('input[name^="properties[_scents_"]:checked'));
    
    // Enforce max 3 scents
    if (checked.length > 3) {
      const oldest = checked[0];
      oldest.checked = false;
    }

    this.updateScentCounter();
    this.updateNextButton();
  }

  updateScentCounter() {
    const checked = this.querySelectorAll('input[name^="properties[_scents_"]:checked').length;
    const counter = this.querySelector('.selected-count');
    if (counter) {
      counter.textContent = checked;
    }
  }

  // Step 2: Engraving Option handlers
  handleEngravingOptionChange() {
    const selectedOption = this.querySelector('input[name="properties[_engraving_option]"]:checked');
    if (selectedOption) {
      this.selectedEngravingOption = {
        value: selectedOption.value,
        label: selectedOption.dataset.label || selectedOption.value
      };
      
      // Clear data from paths not taken
      this.clearUnusedPathData();
      
      // Auto-progress on first selection in Step 2
      if (!this.completedStepActions.has(2)) {
        this.completedStepActions.add(2);
        // Small delay to allow UI to update
        setTimeout(() => {
          this.handleNextClick();
        }, 50);
      }
    }
    this.updateNextButton();
    this.displayStep(this.currentStep);
  }

  clearUnusedPathData() {
    if (!this.selectedEngravingOption) return;

    // Clear all data except scents whenever engraving option changes
    // Reset theme to "view-all" as default
    this.selectedTheme = { 
      value: 'view-all', 
      label: this.t('step_3.view_all_images') || 'View all images'
    };
    this.selectedDesign = [];
    this.engravingText = '';
    this.engravingTexts = [];
    this.uploadedFile = null;
    
    // Clear UI selections
    this.clearThemeSelection();
    this.clearDesignSelection();
    this.clearEngravingInput();
    this.clearUploadPreview();
  }

  clearThemeSelection() {
    this.querySelectorAll('input[name="properties[_theme]"]').forEach(input => {
      input.checked = false;
    });
    // Re-select the "view-all" option as default
    const viewAllRadio = this.querySelector('input[name="properties[_theme]"][value="view-all"]');
    if (viewAllRadio) {
      viewAllRadio.checked = true;
    }
  }

  clearDesignSelection() {
    this.querySelectorAll('input[name="properties[_design]"]').forEach(input => {
      input.checked = false;
    });
    
    // Clear selection order when clearing designs
    this.designSelectionOrder = [];
    
    // Update badges to hide them
    if (this.enableMultipleDesigns) {
      this.updateSelectionOrderBadges();
    }
  }

  clearEngravingInput() {
    // Clear single input mode
    const engravingInput = this.querySelector('[name="properties[_engraving]"]');
    if (engravingInput) {
      engravingInput.value = '';
      engravingInput.style.borderColor = '';
    }
    
    // Clear multi-input mode
    const multiEngravingInputs = this.querySelectorAll('.engraving-input--multi');
    if (multiEngravingInputs.length > 0) {
      multiEngravingInputs.forEach(input => {
        input.value = '';
        input.style.borderColor = '';
      });
      
      // Reset individual counters
      const individualCounters = this.querySelectorAll('.text-counter--individual .text-count');
      individualCounters.forEach(counter => {
        counter.textContent = '0';
      });
    }
    
    // Reset global counter
    const textCountElement = this.querySelector('.text-count');
    if (textCountElement) {
      textCountElement.textContent = '0';
      textCountElement.style.color = '';
    }
    
    // Hide warning message
    const warningElement = this.querySelector('.engraving-warning');
    if (warningElement) {
      warningElement.style.display = 'none';
    }

    // Hide simulation notices
    const simulationNotices = this.querySelectorAll('.engraving-simulation-notice');
    simulationNotices.forEach(notice => {
      notice.style.display = 'none';
    });
    
    // Hide error messages (both single and multi)
    const errorElements = this.querySelectorAll('.engraving-error');
    errorElements.forEach(errorElement => {
      errorElement.style.display = 'none';
    });
  }

  clearUploadPreview() {
    const { uploadPreview, uploadPlaceholder, uploadPreviewImage, uploadPreviewContainer, uploadPdfPlaceholder, uploadPdfFilename, uploadArea, uploadFileInput, uploadDesignRadio } = this.refs;
    
    // Clear the actual file input
    if (uploadFileInput && !Array.isArray(uploadFileInput)) {
      uploadFileInput.value = '';
    }
    
    // Uncheck the upload design radio button
    if (uploadDesignRadio && !Array.isArray(uploadDesignRadio)) {
      uploadDesignRadio.checked = false;
    }
    
    // Hide PDF success indicator in Step 8
    if (uploadPreview && !Array.isArray(uploadPreview)) {
      uploadPreview.classList.add('hidden');
    }
    
    if (uploadPlaceholder && !Array.isArray(uploadPlaceholder)) {
      uploadPlaceholder.style.display = '';
    }

    if (uploadPreviewImage && !Array.isArray(uploadPreviewImage)) {
      uploadPreviewImage.src = '';
      uploadPreviewImage.classList.add('hidden');
    }

    if (uploadPreviewContainer && !Array.isArray(uploadPreviewContainer)) {
      uploadPreviewContainer.classList.add('hidden');
    }
    
    if (uploadPdfPlaceholder && !Array.isArray(uploadPdfPlaceholder)) {
      uploadPdfPlaceholder.classList.add('hidden');
    }
    
    if (uploadPdfFilename && !Array.isArray(uploadPdfFilename)) {
      uploadPdfFilename.textContent = '';
    }

    if (uploadArea && !Array.isArray(uploadArea)) {
      uploadArea.style.display = '';
    }
  }

  // Step 3: Theme handlers
  handleThemeChange() {
    if (!this.completedStepActions.has(this.currentStep)) {
        this.completedStepActions.add(this.currentStep);
        // Small delay to allow UI to update
        setTimeout(() => {
          this.handleNextClick();
        }, 50);
      }

    this.updateNextButton();
  }

  // Step 3: Design handlers
  handleDesignChange(e) {
    if (this.enableMultipleDesigns) {
      const clickedInput = e?.target;
      
      // Update selection order tracking
      if (clickedInput) {
        const value = clickedInput.value;
        
        if (clickedInput.checked) {
          // Add to selection order if not already present
          if (!this.designSelectionOrder.includes(value)) {
            this.designSelectionOrder.push(value);
          }
        } else {
          // Remove from selection order when unchecked
          this.designSelectionOrder = this.designSelectionOrder.filter(v => v !== value);
        }
      }
      
      const checked = Array.from(this.querySelectorAll('input[name="properties[_design]"]:checked'));
      
      // Enforce max designs using actual selection order
      if (checked.length > this.maxDesigns) {
        // Find the oldest selection (first in order) and uncheck it
        const oldestValue = this.designSelectionOrder[0];
        const oldestInput = checked.find(input => input.value === oldestValue);
        
        if (oldestInput) {
          oldestInput.checked = false;
          // Remove from selection order
          this.designSelectionOrder.shift();
        }
      }
      
      // Update visual selection order badges
      this.updateSelectionOrderBadges();
    }
    this.updateNextButton();
    this.updateCandlePreview();
  }

  updateSelectionOrderBadges() {
    // Get all design inputs
    const allDesignInputs = this.querySelectorAll('input[name="properties[_design]"]');
    
    // Get option colors from data attribute on this component
    let optionColors = [];
    if (this.dataset.optionColors) {
      try {
        optionColors = JSON.parse(this.dataset.optionColors);
      } catch (e) {
        console.warn('Failed to parse option colors:', e);
      }
    }
    
    allDesignInputs.forEach(input => {
      const wrapper = input.closest('.design-option');
      if (!wrapper) return;
      
      const badge = wrapper.querySelector('.selection-order-badge');
      if (!badge) return;
      
      // Find position in selection order
      const position = this.designSelectionOrder.indexOf(input.value);
      
      if (position >= 0 && input.checked) {
        // Show badge with number (1-indexed)
        badge.textContent = String(position + 1);
        badge.setAttribute('data-visible', 'true');
        badge.style.display = 'flex';
        
        // Apply color from option if available
        const color = optionColors[position] || '#000';
        badge.style.setProperty('background-color', color, 'important');
      } else {
        // Hide badge
        badge.setAttribute('data-visible', 'false');
        badge.style.display = 'none';
      }
    });
  }

  // Step 5: Engraving input listener setup
  setupEngravingInputListeners() {
    // Single input mode
    const singleEngravingInput = this.querySelector('[name="properties[_engraving]"]');
    if (singleEngravingInput) {
      singleEngravingInput.addEventListener('input', (e) => this.handleEngravingInput(e));
    }

    // Multiple input mode
    const multiEngravingInputs = this.querySelectorAll('.engraving-input--multi');
    if (multiEngravingInputs.length > 0) {
      multiEngravingInputs.forEach((input, index) => {
        input.addEventListener('input', (e) => this.handleMultiEngravingInput(e, index + 1));
      });
    }
  }

  // Step 5: Multi-input handler
  handleMultiEngravingInput(e, inputIndex) {
    const input = e.target;
    const inputGroup = input.closest('.engraving-input-group');
    
    // Validate to exclude emojis only (allow all other characters including newlines)
    const hasEmoji = /[\u{1F000}-\u{1F9FF}\u{2600}-\u{27BF}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}]/u.test(input.value);
    const isValid = !hasEmoji;
    let errorElement = inputGroup?.querySelector('.engraving-error');
    if (!errorElement && inputGroup) {
      errorElement = document.createElement('div');
      errorElement.className = 'engraving-error';
      errorElement.style.marginTop = '0.5rem';
      inputGroup.appendChild(errorElement);
    }
    
    // Show/hide error based on validation
    if (errorElement) {
      if (!isValid && input.value.length > 0) {
        errorElement.textContent = this.t('errors.invalid_characters');
        errorElement.style.display = 'block';
        input.style.borderColor = 'red';
      } else {
        errorElement.style.display = 'none';
        input.style.borderColor = '';
      }
    }
    
    // Count comma-separated texts in THIS specific input
    const texts = input.value.split(',').filter(text => text.trim() !== '');
    const textCount = texts.length;

    this.updateEngravingSimulationNotice(input);
    
    // Update counter for THIS specific input
    const individualCounter = inputGroup?.querySelector(`.text-count[data-input-index="${inputIndex}"]`);
    if (individualCounter) {
      individualCounter.textContent = input.value.trim() === '' ? '0' : String(textCount);
    }
    
    // Check if THIS input exceeds quantity and show warning
    const maxAllowed = this.selectedQuantity;
    const individualMaxCount = inputGroup?.querySelector('.max-text-count');
    if (individualMaxCount) {
      individualMaxCount.textContent = String(maxAllowed);
    }
    
    // Get or create warning message element for this specific input
    let warningElement = inputGroup?.querySelector('.engraving-warning');
    if (!warningElement && inputGroup) {
      warningElement = document.createElement('s');
      warningElement.className = 'engraving-warning';
      warningElement.style.display = 'none';
      inputGroup.appendChild(warningElement);
    }
    
    // Show/hide warning if this input exceeds quantity
    if (warningElement && individualCounter) {
      if (textCount > maxAllowed && input.value.trim() !== '') {
        individualCounter.style.color = 'red';
        warningElement.style.display = 'block';
        const neededQuantity = textCount;
        warningElement.textContent = this.t('engravingWarning', {
          textCount: textCount,
          currentQuantity: maxAllowed,
          extraTexts: textCount - maxAllowed,
          neededQuantity: neededQuantity
        });
      } else {
        individualCounter.style.color = '';
        warningElement.style.display = 'none';
      }
    }
    
    this.updateNextButton();
    this.autoResizeTextarea(input);
    this.updateCandlePreview();
  }

  // Step 4: Engraving handlers (single input mode)
  handleEngravingInput(e) {
    const input = e.target;
    
    // Validate to exclude emojis only (allow all other characters including newlines)
    const hasEmoji = /[\u{1F000}-\u{1F9FF}\u{2600}-\u{27BF}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}]/u.test(input.value);
    const isValid = !hasEmoji;
    
    // Get or create error message element
    let errorElement = this.querySelector('.engraving-error');
    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'engraving-error';
      errorElement.style.color = 'red';
      errorElement.style.fontSize = '0.875rem';
      errorElement.style.marginTop = '0.5rem';
      errorElement.style.display = 'none';
      input.parentElement.appendChild(errorElement);
    }
    
    // Show/hide error based on validation
    if (!isValid && input.value.length > 0) {
      errorElement.textContent = this.t('errors.invalid_characters');
      errorElement.style.display = 'block';
      input.style.borderColor = 'red';
    } else {
      errorElement.style.display = 'none';
      input.style.borderColor = '';
    }
    
    // Count texts separated by commas
    const texts = input.value.split(',').filter(text => text.trim() !== '');
    const textCount = texts.length;

    this.updateEngravingSimulationNotice(input);
    
    // Update counter
    const textCountElement = this.querySelector('.text-count');
    if (textCountElement) {
      textCountElement.textContent = textCount;
    }
    
    // Get warning message element
    const warningElement = this.querySelector('.engraving-warning');
    
    // Validate against quantity
    const maxTextCountElement = this.querySelector('.max-text-count');
    if (maxTextCountElement) {
      const maxTexts = parseInt(maxTextCountElement.textContent) || this.getMaxEngravingCount();
      
      // Add visual feedback if exceeding limit
      if ((textCount > maxTexts) && warningElement && textCountElement) {
        textCountElement.style.color = 'red';
        // Show warning message
        warningElement.style.display = 'block';
        const extraTexts = textCount - maxTexts;
        const neededQuantity = textCount;
          warningElement.textContent = this.t('engravingWarning', {
            textCount: textCount,
            currentQuantity: maxTexts,
            extraTexts: extraTexts,
            neededQuantity: neededQuantity
          });
      } else {
        textCountElement.style.color = '';
        // Hide warning message
        if (warningElement) {
          warningElement.style.display = 'none';
        }
      }
    }
    
    this.updateNextButton();
    this.autoResizeTextarea(input);
    this.updateCandlePreview();
  }

  updateEngravingSimulationNotice(input) {
    const wrapper = input.closest('.engraving-input-group, .engraving-input-wrapper');
    const notice = wrapper?.querySelector('.engraving-simulation-notice');
    if (!notice) return;

    notice.textContent = this.t('engravingSimulationNotice');
    notice.style.display = input.value.includes(',') ? 'block' : 'none';
  }

  // Navigation
  openPopup() {
    this.dataset.open = '';
    document.body.style.overflow = 'hidden';
    this.popupInstanceId = `psp_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    this.hasTrackedCompletionForJourney = false;
    
    // Generate a unique ID for this personalization session
    this.logoLinkId = `logo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Generate a unique ID for upsell product linking
    this.upsellLinkId = `upsell_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Reset upsell state
    this.upsellSelected = false;
    this.resetUpsellCheckbox();
    
    // Reset completed step actions tracking for auto-progression
    this.completedStepActions.clear();
    
    // Get current quantity from product form
    this.updateQuantityFromForm();
    
    // Update max text count based on quantity
    this.updateMaxTextCount();
    
    // Initialize theme selection to "view-all" by default
    this.selectedTheme = {
      value: 'view-all',
      label: this.t('step_3.view_all_images') || 'View all images'
    };
    
    // Reset to step 2 (engraving option), or first enabled step
    this.currentStep = 2;
    if (!this.isStepEnabled(this.currentStep)) {
      const next = this.getNextStep(this.currentStep);
      if (next) {
        this.currentStep = next;
      } else {
        // Fallback: try to find ANY enabled step if the flow is broken
        const enabledStep = [2, 3, 4, 5, 8, 9, 1, 7, 6].find(s => this.isStepEnabled(s));
        if (enabledStep) {
          this.currentStep = enabledStep;
        }
      }
    }

    this.displayStep(this.currentStep);
    this.updateUI();
  }

  closePopup() {
    delete this.dataset.open;
    document.body.style.overflow = '';
  }

  handleNextClick() {
    const stepConfig = this.getStepConfig(this.currentStep);
    
    // Validate if required
    if (stepConfig.required && !this.validateCurrentStep()) {
      this.showValidationError();
      return;
    }

    // Save current step data
    this.saveCurrentStepData();

    // Get the next step based on current step and selections
    const nextStep = this.getNextStep();

    // If no next step (we're at review), add to cart
    if (nextStep === null) {
      this.handleAddToCart();
      return;
    }

    // Move to next step
    this.currentStep = nextStep;

    // Handle theme-based design filtering for step 4 (catalog design)
    if (this.currentStep === 4) {
      const allDesigns = this.querySelectorAll('.design-option--default');
      
      // Clear any previously selected designs when entering step 4
      // This ensures hidden selections don't block new selections
      this.selectedDesign = [];
      this.designSelectionOrder = [];
      
      // If "view-all" is selected or no theme selected, show all designs
      if (!this.selectedTheme || this.selectedTheme.value === 'view-all') {
        for (const design of allDesigns) {
          design.classList.remove('hidden');
        }
      } else {
        // Hide all designs first and uncheck hidden radio buttons
        for (const design of allDesigns) {
          design.classList.add('hidden');
          // Uncheck the radio/checkbox input when hiding
          const input = design.querySelector('input[name="properties[_design]"]');
          if (input) {
            input.checked = false;
          }
        }
        // Show only designs matching the selected theme
        let designs = this.querySelectorAll(`[data-group-label="${this.selectedTheme?.label}"]`);
        for (const design of designs) {
          design.classList.remove('hidden');
        }
      }
      
      // Update selection order badges when entering step 4
      if (this.enableMultipleDesigns) {
        this.updateSelectionOrderBadges();
      }
    }

    this.displayStep(this.currentStep);
    this.updateUI();
    this.scrollToTop();
  }

  getNextStep(fromStep = this.currentStep) {
    let nextStep = null;
    // Step routing logic based on engraving option
    // Flow: 2 -> [3 -> 4] / [8] -> 5 -> 9 (preview) -> 1 (scents) -> 7 (upsell) -> 6 (review)
    // Own-design path skips preview: 2 -> 8 -> 1 -> 7 -> 6
    switch (fromStep) {
      case 2: // Engraving Option -> Dynamic routing
        if (!this.selectedEngravingOption) {
          nextStep = 3; // Default to theme if nothing selected
          break;
        }
        
        switch (this.selectedEngravingOption.value) {
          case 'catalog-design':
            nextStep = 3; // Go to Theme selection
            break;
          case 'text-without-design':
            nextStep = 5; // Skip to Engraving text
            break;
          case 'my-design':
            nextStep = 8; // Go to Upload Design
            break;
          default:
            nextStep = 3;
        }
        break;
      
      case 3: // Theme -> Design (only for catalog-design path)
        nextStep = 4;
        break;
      
      case 4: // Design -> Engraving text
        nextStep = 5;
        break;
      
      case 5: // Engraving text -> Preview (Step 9)
        nextStep = 9;
        break;
      
      case 8: // Upload Design -> Scents (skip preview for own-design path)
        nextStep = 1;
        break;
      
      case 9: // Preview -> Scents (Step 1)
        nextStep = 1;
        break;
      
      case 1: // Scents -> Upsell (Step 7)
        nextStep = 7;
        break;
      
      case 7: // Upsell -> Review
        nextStep = 6;
        break;
      
      case 6: // Review -> Add to cart
        nextStep = null;
        break;
      
      default:
        nextStep = fromStep + 1;
    }

    // Recursively find the next enabled step
    if (nextStep !== null && !this.isStepEnabled(nextStep)) {
      return this.getNextStep(nextStep);
    }

    return nextStep;
  }

  previousStep() {
    const prevStep = this.getPreviousStep();
    if (prevStep !== null) {
      this.currentStep = prevStep;
      this.displayStep(this.currentStep);
      this.updateUI();
      this.scrollToTop();
    }
  }

  getPreviousStep(fromStep = this.currentStep) {
    let prevStep = null;
    // Reverse step routing logic
    // Flow: 2 -> [3 -> 4] / [8] -> 5 -> 9 (preview) -> 1 (scents) -> 7 (upsell) -> 6 (review)
    // Own-design path skips preview: 2 -> 8 -> 1 -> 7 -> 6
    switch (fromStep) {
      case 2: // Engraving Option -> No previous (first step)
        prevStep = null;
        break;
      
      case 3: // Theme -> Engraving Option (only for catalog-design)
        prevStep = 2;
        break;
      
      case 4: // Design -> Theme
        prevStep = 3;
        break;
      
      case 5: // Engraving text -> Dynamic based on engraving option
        if (!this.selectedEngravingOption) {
          prevStep = 2;
          break;
        }
        
        switch (this.selectedEngravingOption.value) {
          case 'catalog-design':
            prevStep = 4; // Back to Design
            break;
          case 'text-without-design':
            prevStep = 2; // Back to Engraving Option
            break;
          default:
            prevStep = 2;
        }
        break;
      
      case 9: // Preview -> Dynamic based on engraving option
        if (!this.selectedEngravingOption) {
          prevStep = 5;
          break;
        }
        
        switch (this.selectedEngravingOption.value) {
          case 'catalog-design':
            prevStep = 5; // Back to Engraving text
            break;
          case 'text-without-design':
            prevStep = 5; // Back to Engraving text
            break;
          case 'my-design':
            prevStep = 8; // Back to Upload Design
            break;
          default:
            prevStep = 5;
        }
        break;
      
      case 1: // Scents -> Preview (Step 9) or Upload Design for own-design path
        prevStep = this.selectedEngravingOption?.value === 'my-design' ? 8 : 9;
        break;
      
      case 7: // Upsell -> Scents (Step 1)
        prevStep = 1;
        break;
      
      case 6: // Review -> Upsell (Step 7)
        prevStep = 7;
        break;
      
      case 8: // Upload Design -> Engraving Option
        prevStep = 2;
        break;
      
      default:
        prevStep = fromStep - 1;
    }

    if (prevStep !== null && !this.isStepEnabled(prevStep)) {
      return this.getPreviousStep(prevStep);
    }

    return prevStep;
  }

  displayStep(stepNumber) {
    const steps = this.querySelectorAll('[data-step]');
    steps.forEach((step) => {
      const stepNum = parseInt(step.dataset.step);
      if (stepNum === stepNumber) {
        step.classList.add('personalized-steps-popup__step--active');
      } else {
        step.classList.remove('personalized-steps-popup__step--active');
      }
    });
    // Update max text count if we are on the engraving step
    if (stepNumber === 5) {
      this.updateMaxTextCount();
      // Reset auto-height on all engraving textareas when entering the step
      this.querySelectorAll('.engraving-input').forEach(ta => this.autoResizeTextarea(ta));
    }

    // Show/hide the shared candle preview.
    const { candlePreviewContainer } = this.refs;
    if (candlePreviewContainer && !Array.isArray(candlePreviewContainer)) {
      if (this.shouldHideCandlePreview(stepNumber)) {
        candlePreviewContainer.classList.add('hidden');
      } else {
        candlePreviewContainer.classList.remove('hidden');
      }
    }

    // Update candle preview on every step
    this.updateCandlePreview();
    this.trackStepSeen(stepNumber);
  }

  getFlowType() {
    switch (this.selectedEngravingOption?.value) {
      case 'catalog-design':
        return 'catalog_design';
      case 'text-without-design':
        return 'text_without_design';
      case 'my-design':
        return 'my_design';
      default:
        return 'unknown';
    }
  }

  getCurrentVariantId() {
    const productForm = this.closest('form[data-type="add-to-cart-form"]') ||
      this.closest('form[action*="/cart/add"]') ||
      document.querySelector('[data-type="add-to-cart-form"]') ||
      document.querySelector('form[action*="/cart/add"]');
    const variantInput = productForm?.querySelector('input[name="id"]') ||
      productForm?.querySelector('select[name="id"]') ||
      productForm?.closest('product-form-component')?.querySelector('input[name="id"]');
    const variantId = parseInt(variantInput?.value, 10);

    return Number.isNaN(variantId) ? null : variantId;
  }

  getSelectedDesignCount() {
    if (Array.isArray(this.selectedDesign) && this.selectedDesign.length > 0) {
      return this.selectedDesign.length;
    }

    return this.querySelectorAll('input[name="properties[_design]"]:checked').length;
  }

  getSelectedDesignNumbers() {
    if (!this.selectedDesign || (Array.isArray(this.selectedDesign) && this.selectedDesign.length === 0)) {
      return [];
    }

    const designs = Array.isArray(this.selectedDesign) ? this.selectedDesign : [this.selectedDesign];

    if (designs[0]?.value === 'custom-upload') {
      return [];
    }

    const pattern = /\b(?:[A-Za-z]+-(\d+)|(\d+)-[A-Za-z]+)\b/;

    return designs
      .map((design) => {
        const match = design.label?.match(pattern);
        return match?.[1] || match?.[2] || null;
      })
      .filter(Boolean);
  }

  buildAnalyticsPayload(stepNumber = null) {
    const selectedDesignNumbers = this.getSelectedDesignNumbers();
    const payload = {
      flow_type: this.getFlowType(),
      path_version: 'personalized_steps_v1',
      popup_instance_id: this.popupInstanceId,
      product_handle: this.dataset.productHandle || undefined,
      product_variant_id: this.getCurrentVariantId(),
      multiple_designs_enabled: this.enableMultipleDesigns,
      max_designs: this.maxDesigns,
      selected_quantity: this.selectedQuantity,
      selected_design_count: this.getSelectedDesignCount(),
      upsell_selected: this.upsellSelected,
      upsell_quantity: this.upsellSelected ? this.upsellQuantity : undefined,
    };

    if (selectedDesignNumbers.length > 0) {
      payload.selected_design_numbers = selectedDesignNumbers;
    }

    if (stepNumber !== null) {
      payload.step_number = stepNumber;
      payload.step_key = STEP_KEYS[stepNumber];
      payload.step_name = this.getStepTitle(stepNumber);
      payload.step_required = this.getStepConfig(stepNumber).required === true;
    }

    if (this.selectedTheme?.value && this.selectedTheme.value !== 'view-all') {
      payload.selected_theme = this.selectedTheme.value;
    }

    return payload;
  }

  capturePosthogEvent(eventName, payload) {
    if (!eventName || typeof window === 'undefined' || typeof window.posthog?.capture !== 'function') {
      return;
    }

    window.posthog.capture(eventName, payload);
  }

  trackStepSeen(stepNumber) {
    const eventName = STEP_EVENTS[stepNumber];
    const dedupeKey = `${this.dataset.blockId || 'personalized-steps'}:${stepNumber}`;

    if (!eventName || seenStepsThisTab.has(dedupeKey)) {
      return;
    }

    seenStepsThisTab.add(dedupeKey);
    
    this.capturePosthogEvent(eventName, this.buildAnalyticsPayload(stepNumber));
  }

  trackCompletedJourney() {
    if (this.hasTrackedCompletionForJourney) {
      return;
    }

    this.hasTrackedCompletionForJourney = true;
    this.capturePosthogEvent('personalized_steps_completed', this.buildAnalyticsPayload(6));
  }

  shouldHideCandlePreview(stepNumber) {
    // Custom uploaded designs do not have a reliable candle simulation.
    if (this.selectedEngravingOption?.value === 'my-design') {
      return true;
    }

    const hidePreviewSteps = [1, 3, 7]; // 1 = scents, 3 = theme, 7 = upsell
    return hidePreviewSteps.includes(stepNumber);
  }

  autoResizeTextarea(textarea) {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  updateUI() {
    this.updatePreviousButton();
    this.updateNextButton();
    this.updateProgress();
  }

  updatePreviousButton() {
    const { prevButton } = this.refs;
    if (prevButton && !Array.isArray(prevButton)) {
      const isDisabled = this.getPreviousStep() === null;
      prevButton.disabled = isDisabled;
      prevButton.setAttribute('aria-disabled', String(isDisabled));
    }
  }

  updateNextButton() {
    const { nextButton } = this.refs;
    if (!nextButton || Array.isArray(nextButton)) return;

    let buttonText;

    const nextStep = this.getNextStep();

    if (nextStep === null) {
      buttonText = this.t('addToCart');
    } else {
      const nextStepTitle = this.getStepTitle(nextStep);
      buttonText = nextStepTitle;
    }

    const { nextButtonText } = this.refs;
    if (nextButtonText && !Array.isArray(nextButtonText)) {
      nextButtonText.textContent = buttonText;
    } else {
      nextButton.textContent = buttonText;
    }
  }

  getStepTitle(stepNumber) {
    const step = this.querySelector(`[data-step="${stepNumber}"]`);
    const stepName = step?.dataset.stepName?.trim();
    const title = step?.querySelector('.step-title')?.textContent?.trim();

    return stepName || title || this.t('nextStep');
  }

  updateProgress() {
    const progressIndicator = this.refs.progressIndicator;
    if (!progressIndicator || Array.isArray(progressIndicator)) return;

    // Calculate dynamic path and current step index
    const path = this.calculateCurrentPath();
    const currentStepIndex = path.indexOf(this.currentStep) + 1;
    const totalSteps = path.length;

    const progressText = progressIndicator.querySelector('span');
    if (progressText) {
      progressText.textContent = this.t('progressIndicator', {
        step: currentStepIndex,
        total: totalSteps
      });
    }
  }

  calculateCurrentPath() {
    // Determine potential full path based on current selections
    // New flow: 2 -> [3 -> 4] / [8] -> 5 -> 9 (preview) -> 1 (scents) -> 7 (upsell) -> 6 (review)
    // Own-design path skips preview: 2 -> 8 -> 1 -> 7 -> 6
    let fullPath = [2, 3, 4, 5, 9, 1, 7, 6]; // Default path (catalog-design)

    if (this.selectedEngravingOption) {
      switch (this.selectedEngravingOption.value) {
        case 'catalog-design':
          fullPath = [2, 3, 4, 5, 9, 1, 7, 6];
          break;
        case 'text-without-design':
          fullPath = [2, 5, 9, 1, 7, 6];
          break;
        case 'my-design':
          fullPath = [2, 8, 1, 7, 6];
          break;
      }
    }

    // Filter by enabled steps
    return fullPath.filter(step => this.isStepEnabled(step));
  }

  getStepConfig(stepNumber) {
    const config = {
      1: { required: true, skippable: false },
      2: { required: true, skippable: false },
      3: { required: false, skippable: true },
      4: { required: true, skippable: false },
      5: { required: false, skippable: true },
      6: { required: false, skippable: false },
      7: { required: false, skippable: true }, // Upsell step
      8: { required: false, skippable: true }, // Upload Design step
      9: { required: false, skippable: false }, // Candle Preview step
    };
    return config[stepNumber] || {};
  }

  currentStepHasInput() {
    switch (this.currentStep) {
      case 1: // Scents
        return this.querySelectorAll('input[name^="properties[_scents_"]:checked').length > 0;
      case 2: // Engraving Option
        return this.querySelector('input[name="properties[_engraving_option]"]:checked') !== null;
      case 3: // Theme
        return this.querySelector('input[name="properties[_theme]"]:checked') !== null;
      case 4: // Design
        return this.querySelector('input[name="properties[_design]"]:checked') !== null;
      case 5: // Engraving Text
        // Check if multi-input mode
        const multiInputs = this.querySelectorAll('.engraving-input--multi');
        if (multiInputs.length > 0) {
          // Check if at least one multi-input has value
          return Array.from(multiInputs).some(input => input.value.trim().length > 0);
        } else {
          // Single input mode
          const input = this.querySelector('[name="properties[_engraving]"]');
          return input && input.value.trim().length > 0;
        }
      case 6: // Review
        return true;
      case 7: // Upsell
        return this.upsellSelected;
      case 8: // Upload Design
        return this.uploadedFile !== null;
      case 9: // Candle Preview
        return true;
      default:
        return false;
    }
  }

  validateCurrentStep() {
    if (this.currentStep === 1) {
      // Must select at least 1 scent
      return this.querySelectorAll('input[name^="properties[_scents_"]:checked').length > 0;
    }
    if (this.currentStep === 2) {
      // Must select an engraving option
      return this.querySelector('input[name="properties[_engraving_option]"]:checked') !== null;
    }
    if (this.currentStep === 4) {
      // Must select at least one design
      return this.querySelectorAll('input[name="properties[_design]"]:checked').length > 0;
    }
    if (this.currentStep === 5) {
      // Check if multi-input mode
      const multiInputs = this.querySelectorAll('.engraving-input--multi');
      if (multiInputs.length > 0) {
        // Multi-input mode: validate each input for emojis and comma-separated count
        for (const input of multiInputs) {
          if (input.value.trim()) {
            const noEmojiRegex = /^(?!.*[\u{1F000}-\u{1F9FF}\u{2600}-\u{27BF}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}]).*$/u;
            if (!noEmojiRegex.test(input.value)) {
              this.validationErrorMessage = this.t('errors.invalid_characters');
              return false;
            }
            
            // Count comma-separated texts in this input
            const texts = input.value.split(',').filter(text => text.trim() !== '');
            const textCount = texts.length;
            const maxAllowed = this.selectedQuantity;
            
            if (textCount > maxAllowed) {
              this.validationErrorMessage = `Un de vos champs contient ${textCount} textes mais votre quantité est de ${maxAllowed}.`;
              return false;
            }
          }
        }
      } else {
        // Single input mode: validate comma-separated text
        const input = this.querySelector('input[name="properties[_engraving]"]');
        if (input && input.value.trim()) {
          // Validate to exclude emojis only (allow all other characters)
          const noEmojiRegex = /^(?!.*[\u{1F000}-\u{1F9FF}\u{2600}-\u{27BF}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}]).*$/u;
          if (!noEmojiRegex.test(input.value)) {
            this.validationErrorMessage = this.t('errors.invalid_characters');
            return false;
          }
          
          // Validate text count
          const texts = input.value.split(',').filter(text => text.trim() !== '');
          const maxAllowed = this.getMaxEngravingCount();
          if (texts.length > maxAllowed) {
            this.validationErrorMessage = this.t('errors.tooManyTexts', {
              count: texts.length,
              max: maxAllowed
            });
            return false;
          }
        }
      }
    }
    if (this.currentStep === 8) {
      // Must upload a design file
      return this.uploadedFile !== null;
    }
    return true;
  }

  showValidationError() {
    // Show error message
    const currentStepElement = this.querySelector(`[data-step="${this.currentStep}"]`);
    if (!currentStepElement) return;

    // Add shake animation or error styling
    currentStepElement.style.animation = 'shake 0.3s';
    setTimeout(() => {
      currentStepElement.style.animation = '';
    }, 300);

    // Show message
    const message = this.validationErrorMessage || this.t('validationError');
    alert(message);
    
    // Reset custom error message
    this.validationErrorMessage = null;
  }

  saveCurrentStepData() {
    console.log('Saving data for step', this.currentStep);
    switch (this.currentStep) {
      case 1: // Scents
        this.selectedScents = Array.from(this.querySelectorAll('input[name^="properties[_scents_"]:checked'))
          .map(input => ({
            value: input.value,
            label: input.dataset.label || input.value
          }));
        console.log(this.selectedScents);
        break;
      case 2: // Engraving Option
        const engravingOptionInput = this.querySelector('input[name="properties[_engraving_option]"]:checked');
        this.selectedEngravingOption = engravingOptionInput ? {
          value: engravingOptionInput.value,
          label: engravingOptionInput.dataset.label || engravingOptionInput.value
        } : null;
        break;
      case 3: // Theme
        const themeInput = this.querySelector('input[name="properties[_theme]"]:checked');
        this.selectedTheme = themeInput ? {
          value: themeInput.value,
          label: themeInput.dataset.label || themeInput.value
        } : null;
        break;
      case 4: // Design
        if (this.enableMultipleDesigns) {
          const designInputs = Array.from(this.querySelectorAll('input[name="properties[_design]"]:checked'));
          this.selectedDesign = designInputs.map(input => ({
            value: input.value,
            label: input.dataset.label || input.value
          }));
        } else {
          const designInput = this.querySelector('input[name="properties[_design]"]:checked');
          this.selectedDesign = designInput ? [{
            value: designInput.value,
            label: designInput.dataset.label || designInput.value
          }] : [];
        }

        console.log({saved: this.selectedDesign})
        break;
      case 5: // Engraving Text
        // Check if multi-input mode
        const multiInputs = this.querySelectorAll('.engraving-input--multi');
        if (multiInputs.length > 0) {
          // Multi-input mode: collect all input values with their option names
          this.engravingTexts = Array.from(multiInputs)
            .map(input => ({
              optionName: input.dataset.optionName || '',
              text: input.value.trim()
            }))
            .filter(item => item.text !== '');
          // Keep engravingText for backward compatibility
          this.engravingText = this.engravingTexts.map(item => item.text).join(', ');
        } else {
          // Single input mode
          const engravingInput = this.querySelector('[name="properties[_engraving]"]');
          this.engravingText = engravingInput ? engravingInput.value.trim() : '';
          this.engravingTexts = [];
        }
        this.updateReviewSummary();
        break;
      case 6: // Review - update summary
        this.updateReviewSummary();
        break;
      case 7: // Upsell - save selection state, quantity and update review
        const { upsellCheckbox: upsellCb, upsellQuantityInput: upsellQtyInput } = this.refs;
        if (upsellCb && !Array.isArray(upsellCb)) {
          this.upsellSelected = upsellCb.checked;
        }
        if (upsellQtyInput && !Array.isArray(upsellQtyInput)) {
          this.upsellQuantity = parseInt(upsellQtyInput.value) || 1;
        }
        this.updateReviewSummary();
        break;
      case 8: // Upload Design - update review summary for my-design path
        this.updateReviewSummary();
        break;
      case 9: // Candle Preview - nothing to save, it's display only
        break;
    }
  }

  updateCandlePreview() {
    const candlePreview = this.refs.candlePreview;
    if (!candlePreview || Array.isArray(candlePreview)) return;

    // Determine the design image URL based on engraving option
    let imageUrl = '';
    if (this.selectedEngravingOption) {
      switch (this.selectedEngravingOption.value) {
        case 'catalog-design': {
          // Get the first selected design's image from DOM
          const checkedDesign = this.querySelector('input[name="properties[_design]"]:checked');
          if (checkedDesign) {
            const wrapper = checkedDesign.closest('.design-option');
            const img = wrapper?.querySelector('.design-image');
            if (img) {
              imageUrl = img.src || '';
            }
          }
          break;
        }
        case 'my-design': {
          // Use the static "my design" image from block settings
          const myDesignImage = candlePreview.dataset.myDesignImage;
          if (myDesignImage) {
            imageUrl = myDesignImage;
          }
          break;
        }
        // 'text-without-design' — no image, only text
      }
    }

    // Determine the engraving text – always take the first comma-separated segment
    let engravedText = '';

    if (this.currentStep === 5) {
      // On the engraving step, read live from the DOM input
      const multiInputs = this.querySelectorAll('.engraving-input--multi');
      if (multiInputs.length > 0) {
        const firstInput = multiInputs[0];
        engravedText = (firstInput?.value || '').split(',')[0].trim();
      } else {
        const singleInput = this.querySelector('[name="properties[_engraving]"]');
        engravedText = (singleInput?.value || '').split(',')[0].trim();
      }
    } else if (this.engravingTexts && this.engravingTexts.length > 0) {
      engravedText = (this.engravingTexts[0]?.text || '').split(',')[0].trim();
    } else if (this.engravingText) {
      engravedText = this.engravingText.split(',')[0].trim();
    }

    // Show placeholder text when on the engraving step with no text yet
    if (!engravedText && this.currentStep === 5) {
      engravedText = this.t('previewPlaceholderText');
    }

    // Update the candle-preview attributes
    candlePreview.dataset.imageUrl = imageUrl;
    candlePreview.dataset.engravedText = engravedText;

    // Re-render the component by triggering connectedCallback logic
    if (candlePreview.shadowRoot) {
      candlePreview.disconnectedCallback?.();
      candlePreview.connectedCallback?.();
    }
  }

  updateReviewSummary() {
    // Quantity
    const quantityReview = this.querySelector('#review-quantity');
    if (quantityReview) {
      const itemKey = this.selectedQuantity === 1 ? 'itemCount.one' : 'itemCount.other';
      quantityReview.textContent = this.t(itemKey, { count: this.selectedQuantity });
    }

    // Scents
    const scentReview = this.querySelector('#review-scent');
    console.log(this.selectedScents);
    if (scentReview && this.selectedScents.length > 0) {
      scentReview.textContent = this.selectedScents.map(s => s.label).join(', ');
    }

    // Engraving Option
    const engravingOptionReview = this.querySelector('#review-engraving-option');
    if (engravingOptionReview) {
      engravingOptionReview.textContent = this.selectedEngravingOption ? this.selectedEngravingOption.label : this.t('notSelected');
    }

    // Theme - only show if catalog-design path and not "view-all"
    const themeReviewItem = this.querySelector('.review-item--theme');
    const themeReview = this.querySelector('#review-theme');
    if (this.selectedEngravingOption && this.selectedEngravingOption.value === 'catalog-design') {
      // Hide theme if "view-all" is selected (it's just a filter, not a real theme)
      if (this.selectedTheme && this.selectedTheme.value === 'view-all') {
        if (themeReviewItem) themeReviewItem.style.display = 'none';
      } else {
        if (themeReviewItem) themeReviewItem.style.display = '';
        if (themeReview) {
          themeReview.textContent = this.selectedTheme ? this.selectedTheme.label : this.t('notSelected');
        }
      }
    } else {
      if (themeReviewItem) themeReviewItem.style.display = 'none';
    }

    // Design - only show if catalog-design path
    const designReviewItem = this.querySelector('.review-item--design');
    const designReview = this.querySelector('#review-design');
    if (this.selectedEngravingOption && this.selectedEngravingOption.value === 'catalog-design') {
      if (designReviewItem) designReviewItem.style.display = '';
      if (designReview) {
        if (Array.isArray(this.selectedDesign) && this.selectedDesign.length > 0) {
          designReview.textContent = this.selectedDesign.map(d => d.label).join(', ');
        } else {
          designReview.textContent = this.t('notSelected');
        }
      }
    } else if (this.selectedEngravingOption && this.selectedEngravingOption.value === 'my-design') {
      // Show upload status for my-design path
      if (designReviewItem) designReviewItem.style.display = '';
      if (designReview) {
        designReview.textContent = this.uploadedFile ? this.t('upload.customDesign') : this.t('notSelected');
      }
    } else {
      if (designReviewItem) designReviewItem.style.display = 'none';
    }

    // Engraving - check if multi-input mode
    const engravingReviewMulti = this.querySelector('#review-engraving-multi');
    const engravingReview = this.querySelector('#review-engraving');
    
    if (engravingReviewMulti && this.engravingTexts && this.engravingTexts.length > 0) {
      // Multi-input mode: show each engraving separately
      engravingReviewMulti.innerHTML = this.engravingTexts
        .map(item => `<p class="review-value"><strong>${item.optionName}:</strong> ${item.text}</p>`)
        .join('');
    } else if (engravingReviewMulti) {
      engravingReviewMulti.innerHTML = `<p class="review-value">${this.t('notProvided')}</p>`;
    }
    
    if (engravingReview) {
      engravingReview.textContent = this.engravingText || this.t('notProvided');
    }

    // Upsell - always show if step 7 is enabled, display selection status
    const upsellReviewItem = this.querySelector('.review-item--upsell');
    const upsellReview = this.querySelector('#review-upsell');
    if (upsellReviewItem && this.isStepEnabled(7)) {
      upsellReviewItem.style.display = '';
      if (upsellReview) {
        if (this.upsellSelected) {
          const qty = this.upsellQuantity || 1;
          upsellReview.textContent = qty > 1 
            ? `${this.t('upsellAdded') || 'Added'} (x${qty})`
            : this.t('upsellAdded') || 'Added';
        } else {
          upsellReview.textContent = this.t('upsellNotAdded') || 'Not added';
        }
      }
    } else if (upsellReviewItem) {
      upsellReviewItem.style.display = 'none';
    }
  }

  updateQuantityFromForm() {
    // Try to find quantity input in various forms
    const quantityInput = document.querySelector('input[name="quantity"]') ||
                         document.querySelector('[name="quantity"]') ||
                         document.querySelector('.quantity-selector input');
    
    if (quantityInput && quantityInput.value) {
      this.selectedQuantity = parseInt(quantityInput.value) || 1;
    } else {
      this.selectedQuantity = 1;
    }
    
    // Update max text count
    this.updateMaxTextCount();
    
    // Update the review immediately
    this.updateReviewSummary();
  }

  getMaxEngravingCount() {
    let multiplier = 1;
    if (this.enableMultipleDesigns && this.maxDesigns > 1) {
      multiplier = this.maxDesigns;
    }
    return this.selectedQuantity * multiplier;
  }

  updateMaxTextCount() {
    // For multi-input mode, max is just the quantity (not quantity * designs)
    const multiInputs = this.querySelectorAll('.engraving-input--multi');
    if (multiInputs.length > 0) {
      // Update ALL individual max-text-count elements
      const individualMaxCountElements = this.querySelectorAll('.text-counter--individual .max-text-count');
      individualMaxCountElements.forEach(element => {
        element.textContent = String(this.selectedQuantity);
      });
    } else {
      // For single input mode, use the original calculation
      const maxTextCountElement = this.querySelector('.max-text-count');
      if (maxTextCountElement) {
        maxTextCountElement.textContent = String(this.getMaxEngravingCount());
      }
    }
  }

  setLoadingState(loading) {
    const { nextButton } = this.refs;
    if (!nextButton || Array.isArray(nextButton)) return;

    if (loading) {
      nextButton.setAttribute('data-loading', '');
      nextButton.setAttribute('disabled', 'true');
    } else {
      nextButton.removeAttribute('data-loading');
      nextButton.removeAttribute('disabled');
    }
  }

  async handleAddToCart() {
    this.setLoadingState(true);

    // Find product form — the popup is rendered inside the form, so closest() is most reliable.
    // Fall back to document-level queries if the DOM structure differs.
    const productForm = this.closest('form[data-type="add-to-cart-form"]') ||
                        this.closest('form[action*="/cart/add"]') ||
                        document.querySelector('[data-type="add-to-cart-form"]') || 
                        document.querySelector('form[action*="/cart/add"]');
    
    if (!productForm) {
      console.error('Product form not found');
      this.setLoadingState(false);
      alert(this.t('errors.formNotFound'));
      return;
    }

    // Get main product variant ID and quantity from form
    const variantInput = productForm.querySelector('input[name="id"]') || 
                         productForm.querySelector('select[name="id"]') ||
                         productForm.closest('product-form-component')?.querySelector('input[name="id"]');
    const quantityInput = productForm.querySelector('input[name="quantity"]');
    
    if (!variantInput) {
      console.error('Variant input not found in form:', productForm);
      this.setLoadingState(false);
      alert(this.t('errors.formNotFound'));
      return;
    }

    const mainVariantId = parseInt(variantInput.value);
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

    // Generate unique link ID for property-based linking
    const linkId = Date.now().toString(36) + Math.random().toString(36).substr(2, 9);
    
    // Store linkIds for injectPersonalizationData to use
    if (this.upsellSelected && this.upsellVariantId) {
      this.upsellLinkId = linkId;
    }
    if (this.selectedEngravingOption?.value === 'my-design' && this.customDesignVariantId) {
      this.logoLinkId = linkId;
    }

    // Inject personalization data into form (this sets all properties including link IDs)
    this.injectPersonalizationData(productForm);

    // Collect form data as properties for main product
    const formData = new FormData(productForm);
    const mainProperties = {};
    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[') && value) {
        const propName = key.replace('properties[', '').replace(']', '');
        mainProperties[propName] = value;
      }
    }

    const cartAddUrl = window.Shopify?.routes?.root 
      ? `${window.Shopify.routes.root}cart/add.js` 
      : '/cart/add.js';

    try {
      // Helper to build child items with the correct parent reference
      const buildChildItems = (parentRef) => {
        const items = [];
        if (this.upsellSelected && this.upsellVariantId) {
          items.push({
            id: parseInt(this.upsellVariantId),
            quantity: this.upsellQuantity || 1,
            ...parentRef,
            properties: { '_upsell_link_id': linkId }
          });
        }
        if (this.selectedEngravingOption?.value === 'my-design' && this.customDesignVariantId) {
          items.push({
            id: parseInt(this.customDesignVariantId),
            quantity: 1,
            ...parentRef,
            properties: { '_logo_link_id': linkId }
          });
        }
        return items;
      };

      let mainResponse;
      let parsedResult;

      if (this.uploadedFile) {
        // === File upload path ===
        // Main product must be added alone via FormData (multipart/form-data)
        // so Shopify processes the file and generates a CDN link.
        const multipartData = new FormData();
        multipartData.append('id', String(mainVariantId));
        multipartData.append('quantity', String(quantity));

        for (const [key, value] of Object.entries(mainProperties)) {
          multipartData.append(`properties[${key}]`, value);
        }

        multipartData.append('properties[Image sur mesure]', this.uploadedFile);

        mainResponse = await fetch(cartAddUrl, {
          method: 'POST',
          body: multipartData,
        });

        if (!mainResponse.ok) {
          const errorData = await mainResponse.json();
          console.error('Error adding main product to cart:', errorData);
          this.setLoadingState(false);
          alert(errorData.description || this.t('errors.formNotFound'));
          return;
        }

        const mainResult = await mainResponse.json();
        console.log('Main product added to cart:', mainResult);

        // Parent already exists in cart → use parent_line_key (string = line item key)
        const childItems = buildChildItems({ parent_line_key: mainResult.key });

        if (childItems.length > 0) {
          const childResponse = await fetch(cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: childItems }),
          });

          if (!childResponse.ok) {
            console.error('Error adding child products to cart:', await childResponse.json());
          } else {
            console.log('Child products added to cart:', await childResponse.json());
          }
        }

        parsedResult = mainResult;
      } else {
        // === No file upload path ===
        // All items in a single request → use parent_id (integer = variant ID of parent in same request)
        const childItems = buildChildItems({ parent_id: mainVariantId });
        const allItems = [
          { id: mainVariantId, quantity: quantity, properties: mainProperties },
          ...childItems
        ];

        mainResponse = await fetch(cartAddUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: allItems }),
        });

        if (!mainResponse.ok) {
          const errorData = await mainResponse.json();
          console.error('Error adding products to cart:', errorData);
          this.setLoadingState(false);
          alert(errorData.description || this.t('errors.formNotFound'));
          return;
        }
      }

      const result = parsedResult || await mainResponse.json();
      console.log('Products added to cart:', result);
      this.trackCompletedJourney();

      // Fetch updated cart to get the full cart state with item_count
      const cartResponse = await fetch(window.Shopify?.routes?.root 
        ? `${window.Shopify.routes.root}cart.js` 
        : '/cart.js');
      const cart = await cartResponse.json();

      // Dispatch the theme's CartAddEvent to trigger cart drawer opening and refresh
      const { CartAddEvent } = await import('@theme/events');
      
      document.dispatchEvent(
        new CartAddEvent(
          cart,
          'personalized-steps-popup',
          {
            source: 'personalized-steps-popup',
            itemCount: cart.item_count,
          }
        )
      );

    } catch (error) {
      console.error('Error adding products to cart:', error);
      this.setLoadingState(false);
      alert(this.t('errors.formNotFound'));
      return;
    }

    this.setLoadingState(false);
    // Close popup
    this.closePopup();
  }

  injectPersonalizationData(form) {
    const visible_scent = form.querySelector("input[name='properties[Parfums]']")
    const visible_theme = form.querySelector("input[name='properties[Theme]']")
    const visible_design = form.querySelector("input[name='properties[Dessin]']")
    const visible_engraving = form.querySelector("input[name='properties[Gravure]']")
    const visible_gravure_option = form.querySelector("input[name='properties[Gravure Option]']")
    
    console.log({x: this.selectedDesign})
    visible_scent.value = this.selectedScents.map(s => s.label).join(', ');
    // Don't save "view-all" as it's just a filter, not a real theme
    visible_theme.value = (this.selectedTheme && this.selectedTheme.value !== 'view-all') ? this.selectedTheme.label : '';
    visible_design.value = (() => {
      if (!this.selectedDesign || (Array.isArray(this.selectedDesign) && this.selectedDesign.length === 0)) {
        return '';
      }
      
      // Normalize to array
      const designs = Array.isArray(this.selectedDesign) ? this.selectedDesign : [this.selectedDesign];
      
      // Handle custom upload case
      if (designs[0].value === 'custom-upload') {
        // Try to get the translated text, fallback to a user-friendly default
        const translatedText = this.t('upload.customDesign');
        return (translatedText && translatedText !== 'upload.customDesign') 
          ? translatedText 
          : 'Custom Design';
      }
      
      // For regular designs, extract the number or return the label
      const pattern = /\b(?:[A-Za-z]+-(\d+)|(\d+)-[A-Za-z]+)\b/;
      
      const values = designs.map(d => {
        const match = d.label?.match(pattern);
        if (match?.[1]) {
          return match[1];
        }
        if (match?.[2]) {
          return match[2];
        }
        return d.label;
      });
      
      return values.join(', ');
    })();
    // Only set the generic Gravure property in single-input mode
    // In multi-input mode, we use individual "Gravure [OptionName]" properties instead
    if (this.engravingTexts && this.engravingTexts.length > 0) {
      // Multi-input mode - don't set generic Gravure property
      visible_engraving.value = '';
    } else {
      // Single-input mode
      visible_engraving.value = this.engravingText;
    }
    visible_gravure_option.value = this.selectedEngravingOption ? this.selectedEngravingOption.label : '';

    // Add visible properties for each engraving option when in multi-input mode
    if (this.engravingTexts && this.engravingTexts.length > 0) {
      this.engravingTexts.forEach((item, index) => {
        if (item.text && item.optionName) {
          // Check if property input already exists
          const propertyName = `properties[Gravure ${item.optionName}]`;
          let propertyInput = form.querySelector(`input[name='${propertyName}']`);
          
          if (!propertyInput) {
            propertyInput = document.createElement('input');
            propertyInput.type = 'hidden';
            propertyInput.name = propertyName;
            form.appendChild(propertyInput);
          }
          
          propertyInput.value = item.text;
        }
      });
    }

    // Add unique ID for "my-design" engraving option to link with custom design product
    if (this.selectedEngravingOption && this.selectedEngravingOption.value === 'my-design') {
      // Check if _logo_link_id input already exists
      let logoLinkInput = form.querySelector("input[name='properties[_logo_link_id]']");
      
      if (!logoLinkInput) {
        // Create hidden input for _logo_link_id
        logoLinkInput = document.createElement('input');
        logoLinkInput.type = 'hidden';
        logoLinkInput.name = 'properties[_logo_link_id]';
        form.appendChild(logoLinkInput);
      }
      
      logoLinkInput.value = this.logoLinkId;
      
      // Also inject _engraving_option for cart handler to identify
      let engravingOptionInput = form.querySelector("input[name='properties[_engraving_option]']");
      
      if (!engravingOptionInput) {
        engravingOptionInput = document.createElement('input');
        engravingOptionInput.type = 'hidden';
        engravingOptionInput.name = 'properties[_engraving_option]';
        form.appendChild(engravingOptionInput);
      }
      
      engravingOptionInput.value = 'my-design';
    }

    // Add unique ID for upsell product linking
    if (this.upsellSelected && this.upsellLinkId) {
      let upsellLinkInput = form.querySelector("input[name='properties[_upsell_link_id]']");
      
      if (!upsellLinkInput) {
        upsellLinkInput = document.createElement('input');
        upsellLinkInput.type = 'hidden';
        upsellLinkInput.name = 'properties[_upsell_link_id]';
        form.appendChild(upsellLinkInput);
      }
      
      upsellLinkInput.value = this.upsellLinkId;

      // Add visible "Packaging" property when upsell is selected
      let packagingInput = form.querySelector("input[name='properties[Packaging]']");
      
      if (!packagingInput) {
        packagingInput = document.createElement('input');
        packagingInput.type = 'hidden';
        packagingInput.name = 'properties[Packaging]';
        form.appendChild(packagingInput);
      }
      
      packagingInput.value = 'Ajoutée';
    }

  }

  submitForm(form) {
    const submitButton = form.querySelector('button[type="submit"]') || form.querySelector('[name="add"]');
    
    if (submitButton) {
      submitButton.click();
    } else {
      form.submit();
    }
  }

  async addUpsellProductToCart() {
    if (!this.upsellVariantId || !this.upsellLinkId) {
      console.warn('Upsell product not configured');
      return;
    }

    try {
      const formData = {
        items: [
          {
            id: this.upsellVariantId,
            quantity: 1,
            properties: {
              _upsell_link_id: this.upsellLinkId,
            },
          },
        ],
      };

      const response = await fetch(window.Shopify?.routes?.root 
        ? `${window.Shopify.routes.root}cart/add.js` 
        : '/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Error adding upsell product to cart:', errorData);
        return;
      }

      console.log('Upsell product added to cart with link ID:', this.upsellLinkId);
    } catch (error) {
      console.error('Error adding upsell product to cart:', error);
    }
  }

  clearAllSelections() {
    // Clear scents
    this.querySelectorAll('input[name^="properties[_scents_"]').forEach(input => {
      input.checked = false;
    });
    this.updateScentCounter();

    // Clear theme
    this.querySelectorAll('input[name="properties[_theme]"]').forEach(input => {
      input.checked = false;
    });

    // Clear design
    this.querySelectorAll('input[name="properties[_design]"]').forEach(input => {
      input.checked = false;
    });

    // Clear engraving
    const engravingInput = this.querySelector('input[name="properties[_engraving]"]');
    if (engravingInput) {
      engravingInput.value = '';
    }
    const charCount = this.querySelector('.char-count');
    if (charCount) {
      charCount.textContent = '0';
    }

    // Reset data
    // this.selectedScents = [];
    // this.selectedTheme = null;
    // this.selectedDesign = null;
    // this.engravingText = '';
  }

  scrollToTop() {
    const { stepContainer } = this.refs;
    if (stepContainer && !Array.isArray(stepContainer)) {
      stepContainer.scrollTop = 0;
    }
  }

  // Zoom Modal Methods
  handleZoomClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    const button = e.currentTarget;
    const zoomTrigger = button.dataset.zoomImage ? button : button.closest('[data-zoom-image]');
    if (!zoomTrigger) return;

    const imageUrl = zoomTrigger.dataset.zoomImage;
    const imageTitle = zoomTrigger.dataset.zoomTitle;
    const showNote = zoomTrigger.dataset.zoomShowNote !== 'false';
    const showTitle = zoomTrigger.dataset.zoomShowTitle !== 'false';
    const mode = zoomTrigger.dataset.zoomMode || 'image';
    
    this.openZoomModal(imageUrl, imageTitle, showNote, showTitle, mode);
  }

  openZoomModal(imageUrl, imageTitle, showNote = true, showTitle = true, mode = 'image') {
    const { zoomModal, zoomImage, zoomTitle } = this.refs;
    
    if (!zoomModal || Array.isArray(zoomModal)) return;
    if (!zoomImage || Array.isArray(zoomImage)) return;
    if (!zoomTitle || Array.isArray(zoomTitle)) return;
    
    const zoomCandlePreview = this.refs.zoomCandlePreview;
    const useCandlePreview = mode === 'candle-preview' && zoomCandlePreview && !Array.isArray(zoomCandlePreview);

    // Set image and title
    zoomImage.src = imageUrl;
    zoomImage.alt = imageTitle;
    zoomImage.style.display = useCandlePreview ? 'none' : '';

    if (useCandlePreview) {
      zoomCandlePreview.dataset.imageUrl = imageUrl;
      zoomCandlePreview.dataset.engravedText = zoomCandlePreview.dataset.defaultText || '';
      zoomCandlePreview.style.display = 'block';

      if (zoomCandlePreview.shadowRoot) {
        zoomCandlePreview.disconnectedCallback?.();
        zoomCandlePreview.connectedCallback?.();
      }
    } else if (zoomCandlePreview && !Array.isArray(zoomCandlePreview)) {
      zoomCandlePreview.style.display = 'none';
    }

    zoomTitle.textContent = imageTitle;
    zoomTitle.style.display = showTitle ? '' : 'none';

    const zoomNote = zoomModal.querySelector('.design-zoom-modal__red-text');
    if (zoomNote) {
      zoomNote.style.display = showNote ? '' : 'none';
    }
    
    // Show modal
    zoomModal.dataset.open = '';
    
    // Add keyboard listener for escape
    document.addEventListener('keydown', this.handleZoomKeydown);
  }

  closeZoomModal() {
    const { zoomModal } = this.refs;
    if (!zoomModal || Array.isArray(zoomModal)) return;
    
    delete zoomModal.dataset.open;
    
    // Remove keyboard listener
    document.removeEventListener('keydown', this.handleZoomKeydown);
  }

  handleZoomKeydown = (e) => {
    if (e.key === 'Escape') {
      this.closeZoomModal();
    }
  }

  // === Upload Functionality ===
  
  setupUploadListeners() {
    const { 
      uploadDesignRadio, 
      uploadFileInput, 
      uploadDropzone,
      uploadConfirmButton,
      uploadCancelButton,
      uploadCloseButton,
      uploadRemoveButton,
      uploadOverlay,
      uploadPreview
    } = this.refs;

    // When upload design option is clicked, open upload modal
    if (uploadDesignRadio && !Array.isArray(uploadDesignRadio)) {
      uploadDesignRadio.addEventListener('change', (e) => {
        if (e.target.checked) {
          this.openUploadModal();
        }
      });

      uploadDesignRadio.addEventListener('click', () => {
        if (uploadDesignRadio.checked) {
          this.openUploadModal();
        }
      });

      uploadDesignRadio.addEventListener('keydown', (event) => {
        if ((event.key === 'Enter' || event.key === ' ') && uploadDesignRadio.checked) {
          event.preventDefault();
          this.openUploadModal();
        }
      });
    }

    // Allow clicking the success preview to reopen the modal for replacements
    const registerModalTrigger = (element) => {
      if (!element || Array.isArray(element)) return;
      element.addEventListener('click', () => {
        if (uploadDesignRadio && !Array.isArray(uploadDesignRadio)) {
          uploadDesignRadio.checked = true;
        }

        this.openUploadModal();
      });
    };

    registerModalTrigger(uploadPreview);

    // File input change
    if (uploadFileInput && !Array.isArray(uploadFileInput)) {
      uploadFileInput.addEventListener('change', (e) => this.handleFileSelect(e));
    }

    // Drag and drop
    if (uploadDropzone && !Array.isArray(uploadDropzone)) {
      uploadDropzone.addEventListener('dragover', (e) => {
        e.preventDefault();
        uploadDropzone.classList.add('dragover');
      });

      uploadDropzone.addEventListener('dragleave', () => {
        uploadDropzone.classList.remove('dragover');
      });

      uploadDropzone.addEventListener('drop', (e) => {
        e.preventDefault();
        uploadDropzone.classList.remove('dragover');
        const files = e.dataTransfer.files;
        if (files.length > 0) {
          this.handleFile(files[0]);
        }
      });
    }

    // Confirm button
    if (uploadConfirmButton && !Array.isArray(uploadConfirmButton)) {
      uploadConfirmButton.addEventListener('click', () => this.confirmUpload());
    }

    // Cancel button
    if (uploadCancelButton && !Array.isArray(uploadCancelButton)) {
      uploadCancelButton.addEventListener('click', () => this.cancelUpload());
    }

    // Close button
    if (uploadCloseButton && !Array.isArray(uploadCloseButton)) {
      uploadCloseButton.addEventListener('click', () => this.cancelUpload());
    }

    // Remove button
    if (uploadRemoveButton && !Array.isArray(uploadRemoveButton)) {
      uploadRemoveButton.addEventListener('click', () => this.removeUploadedFile());
    }

    // Overlay click
    if (uploadOverlay && !Array.isArray(uploadOverlay)) {
      uploadOverlay.addEventListener('click', () => this.cancelUpload());
    }
  }

  openUploadModal() {
    const { uploadModal } = this.refs;
    if (!uploadModal || Array.isArray(uploadModal)) return;

    if (uploadModal.dataset.open !== undefined) return;

    uploadModal.dataset.open = '';

    const { uploadConfirmButton } = this.refs;
    if (uploadConfirmButton && !Array.isArray(uploadConfirmButton)) {
      uploadConfirmButton.disabled = !this.uploadedFile;
    }

    document.addEventListener('keydown', this.handleUploadKeydown);
  }

  closeUploadModal() {
    const { uploadModal } = this.refs;
    if (!uploadModal || Array.isArray(uploadModal)) return;
    
    delete uploadModal.dataset.open;
    document.removeEventListener('keydown', this.handleUploadKeydown);
  }

  handleUploadKeydown = (e) => {
    if (e.key === 'Escape') {
      this.cancelUpload();
    }
  }

  handleFileSelect(e) {
    const files = e.target.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  handleFile(file) {
    // Validate file type
    const validTypes = ['application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert(this.t('errors.invalidFileType'));
      return;
    }

    // Validate file size (20MB max)
    const maxSize = 20 * 1024 * 1024; // 20MB in bytes
    if (file.size > maxSize) {
      alert(this.t('errors.fileTooLarge'));
      return;
    }

    // Store file temporarily
    this.uploadedFile = file;

    // Show preview
    this.showUploadPreview(file);

    // Enable confirm button
    const { uploadConfirmButton } = this.refs;
    if (uploadConfirmButton && !Array.isArray(uploadConfirmButton)) {
      uploadConfirmButton.disabled = false;
    }
  }

  showUploadPreview(file) {
    const { uploadPreviewContainer, uploadPreviewImage, uploadPdfPlaceholder, uploadPdfFilename, uploadArea } = this.refs;
    
    if (!uploadPreviewContainer || Array.isArray(uploadPreviewContainer)) return;
    if (!uploadArea || Array.isArray(uploadArea)) return;

    // Show the preview container
    uploadPreviewContainer.classList.remove('hidden');
    uploadArea.style.display = 'none';

    // For PDF files, show placeholder instead of image preview
    if (file.type === 'application/pdf') {
      if (uploadPdfPlaceholder && !Array.isArray(uploadPdfPlaceholder)) {
        uploadPdfPlaceholder.classList.remove('hidden');
      }
      
      if (uploadPdfFilename && !Array.isArray(uploadPdfFilename)) {
        uploadPdfFilename.textContent = `${this.t('upload.fileName')} ${file.name}`;
      }
      
      // Hide image preview
      if (uploadPreviewImage && !Array.isArray(uploadPreviewImage)) {
        uploadPreviewImage.classList.add('hidden');
      }
    } else {
      // For image files, show image preview (fallback, though we only accept PDFs now)
      if (uploadPreviewImage && !Array.isArray(uploadPreviewImage)) {
        const reader = new FileReader();
        reader.onload = (e) => {
          uploadPreviewImage.src = e.target.result;
          uploadPreviewImage.classList.remove('hidden');
        };
        reader.readAsDataURL(file);
      }
      
      // Hide PDF placeholder
      if (uploadPdfPlaceholder && !Array.isArray(uploadPdfPlaceholder)) {
        uploadPdfPlaceholder.classList.add('hidden');
      }
    }
  }

  removeUploadedFile() {
    const { 
      uploadPreviewContainer, 
      uploadPreviewImage,
      uploadPdfPlaceholder,
      uploadPdfFilename,
      uploadArea, 
      uploadFileInput, 
      uploadConfirmButton 
    } = this.refs;

    // Clear file
    this.uploadedFile = null;
    
    if (uploadFileInput && !Array.isArray(uploadFileInput)) {
      uploadFileInput.value = '';
    }

    // Hide preview container
    if (uploadPreviewContainer && !Array.isArray(uploadPreviewContainer)) {
      uploadPreviewContainer.classList.add('hidden');
    }
    
    // Clear image preview
    if (uploadPreviewImage && !Array.isArray(uploadPreviewImage)) {
      uploadPreviewImage.src = '';
      uploadPreviewImage.classList.add('hidden');
    }
    
    // Clear PDF placeholder
    if (uploadPdfPlaceholder && !Array.isArray(uploadPdfPlaceholder)) {
      uploadPdfPlaceholder.classList.add('hidden');
    }
    
    if (uploadPdfFilename && !Array.isArray(uploadPdfFilename)) {
      uploadPdfFilename.textContent = '';
    }
    
    // Show upload area
    if (uploadArea && !Array.isArray(uploadArea)) {
      uploadArea.style.display = '';
    }

    // Disable confirm button
    if (uploadConfirmButton && !Array.isArray(uploadConfirmButton)) {
      uploadConfirmButton.disabled = true;
    }
  }

  confirmUpload() {
    if (!this.uploadedFile) return;

    const { uploadPreview, uploadPlaceholder } = this.refs;

    // Show PDF success indicator in the design option (Step 8)
    if (uploadPreview && !Array.isArray(uploadPreview)) {
      uploadPreview.classList.remove('hidden');
    }

    // Hide placeholder
    if (uploadPlaceholder && !Array.isArray(uploadPlaceholder)) {
      uploadPlaceholder.style.display = 'none';
    }

    // Update selected design
    this.selectedDesign = [{
      value: 'custom-upload',
      label: this.t('upload.customDesign')
    }];

    // Close modal
    this.closeUploadModal();
    
    // Auto-progress on first upload confirmation in Step 8
    if (!this.completedStepActions.has(8)) {
      this.completedStepActions.add(8);
      // Small delay to allow UI to update
      setTimeout(() => {
        this.handleNextClick();
      }, 50);
    }
  }

  cancelUpload() {
    const { uploadDesignRadio } = this.refs;

    // If no file was confirmed, uncheck the upload radio
    const isCustomUpload = Array.isArray(this.selectedDesign) && 
                           this.selectedDesign.length > 0 && 
                           this.selectedDesign[0].value === 'custom-upload';

    if (!this.uploadedFile || !isCustomUpload) {
      if (uploadDesignRadio && !Array.isArray(uploadDesignRadio)) {
        uploadDesignRadio.checked = false;
      }
    }

    // Close modal
    this.closeUploadModal();
  }

  // === Upsell Functionality ===
  
  setupUpsellListeners() {
    const { upsellCheckbox, upsellQuantityInput, upsellQuantityMinus, upsellQuantityPlus } = this.refs;
    
    if (upsellCheckbox && !Array.isArray(upsellCheckbox)) {
      upsellCheckbox.addEventListener('change', () => this.handleUpsellChange());
    }

    if (upsellQuantityInput && !Array.isArray(upsellQuantityInput)) {
      upsellQuantityInput.addEventListener('input', () => this.handleUpsellQuantityChange());
      upsellQuantityInput.addEventListener('change', () => this.handleUpsellQuantityChange());
    }

    if (upsellQuantityMinus && !Array.isArray(upsellQuantityMinus)) {
      upsellQuantityMinus.addEventListener('click', () => {
        const { upsellQuantityInput } = this.refs;
        if (!upsellQuantityInput || Array.isArray(upsellQuantityInput)) return;
        const current = parseInt(upsellQuantityInput.value) || 1;
        if (current > 1) {
          upsellQuantityInput.value = current - 1;
          this.handleUpsellQuantityChange();
        }
      });
    }

    if (upsellQuantityPlus && !Array.isArray(upsellQuantityPlus)) {
      upsellQuantityPlus.addEventListener('click', () => {
        const { upsellQuantityInput } = this.refs;
        if (!upsellQuantityInput || Array.isArray(upsellQuantityInput)) return;
        const current = parseInt(upsellQuantityInput.value) || 1;
        upsellQuantityInput.value = current + 1;
        this.handleUpsellQuantityChange();
      });
    }
  }

  handleUpsellChange() {
    const { upsellCheckbox, upsellQuantityWrapper, upsellQuantityInput } = this.refs;
    if (!upsellCheckbox || Array.isArray(upsellCheckbox)) return;
    
    // Update selection state based on checkbox
    this.upsellSelected = upsellCheckbox.checked;

    // Show/hide quantity input
    if (upsellQuantityWrapper && !Array.isArray(upsellQuantityWrapper)) {
      if (this.upsellSelected) {
        upsellQuantityWrapper.classList.remove('hidden');
      } else {
        upsellQuantityWrapper.classList.add('hidden');
        // Reset quantity to 1 when unchecked
        if (upsellQuantityInput && !Array.isArray(upsellQuantityInput)) {
          upsellQuantityInput.value = 1;
        }
        this.upsellQuantity = 1;
        this.hideUpsellQuantityWarning();
      }
    }
    
    this.updateNextButton();
  }

  handleUpsellQuantityChange() {
    const { upsellQuantityInput } = this.refs;
    if (!upsellQuantityInput || Array.isArray(upsellQuantityInput)) return;

    let value = parseInt(upsellQuantityInput.value);
    if (isNaN(value) || value < 1) {
      value = 1;
      upsellQuantityInput.value = 1;
    }
    this.upsellQuantity = value;

    // Check against ceil(product quantity / 2) and show/hide warning
    const maxUpsellQuantity = Math.ceil(this.selectedQuantity / 2);
    if (value > maxUpsellQuantity) {
      this.showUpsellQuantityWarning();
    } else {
      this.hideUpsellQuantityWarning();
    }
  }

  showUpsellQuantityWarning() {
    const { upsellQuantityWarning } = this.refs;
    if (!upsellQuantityWarning || Array.isArray(upsellQuantityWarning)) return;

    const maxUpsellQuantity = Math.ceil(this.selectedQuantity / 2);
    upsellQuantityWarning.textContent = this.t('upsellQuantityWarning', {
      upsellQuantity: this.upsellQuantity,
      packagingPlaces: this.upsellQuantity * 2,
      productQuantity: this.selectedQuantity
    });
    upsellQuantityWarning.classList.remove('hidden');
  }

  hideUpsellQuantityWarning() {
    const { upsellQuantityWarning } = this.refs;
    if (!upsellQuantityWarning || Array.isArray(upsellQuantityWarning)) return;
    
    upsellQuantityWarning.classList.add('hidden');
  }

  resetUpsellCheckbox() {
    const { upsellCheckbox, upsellQuantityWrapper, upsellQuantityInput } = this.refs;
    if (!upsellCheckbox || Array.isArray(upsellCheckbox)) return;
    
    upsellCheckbox.checked = false;
    this.upsellSelected = false;
    this.upsellQuantity = 1;

    if (upsellQuantityInput && !Array.isArray(upsellQuantityInput)) {
      upsellQuantityInput.value = 1;
    }
    if (upsellQuantityWrapper && !Array.isArray(upsellQuantityWrapper)) {
      upsellQuantityWrapper.classList.add('hidden');
    }
    this.hideUpsellQuantityWarning();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this.handleZoomKeydown);
    document.removeEventListener('keydown', this.handleUploadKeydown);
  }
}

// Register custom element
customElements.define('personalized-steps-popup-component', PersonalizedStepsPopupComponent);
